import { popup } from '@/ui/popup';
import { cn } from '@/ui/utils';
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import { cva, type VariantProps } from 'cva';

const Tooltip = TooltipPrimitive.Root;
const TooltipProvider = TooltipPrimitive.Provider;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipPortal = TooltipPrimitive.Portal;
const TooltipPositioner = TooltipPrimitive.Positioner;

const tooltipPopup = cva({
  base: [
    popup.base,
    popup.transition,
    'flex items-center gap-px rounded-md px-2 py-1 text-sm has-data-[slot=kbd]:pr-1 has-data-[slot=kbd]:gap-1.5',
  ],
  variants: {
    variant: {
      default:
        'bg-foreground text-primary-foreground outline-foreground [&_.text-muted]:text-white/70 [&_.text-muted-foreground]:text-white/70 [&_[data-slot=kbd]]:bg-white/15 [&_[data-slot=kbd]]:text-white',
      popup: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

interface TooltipPopupProps
  extends React.ComponentProps<typeof TooltipPrimitive.Positioner>,
    VariantProps<typeof tooltipPopup> {
  positionerClassName?: string;
}

function TooltipPopup({
  className,
  children,
  sideOffset = 5,
  positionerClassName = '',
  variant,
  ...props
}: TooltipPopupProps) {
  return (
    <TooltipPortal>
      <TooltipPositioner sideOffset={sideOffset} className={positionerClassName} {...props}>
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(tooltipPopup({ variant }), className)}
        >
          {children}
        </TooltipPrimitive.Popup>
      </TooltipPositioner>
    </TooltipPortal>
  );
}

function TooltipSegments({
  segments,
}: {
  segments: { icon: React.ReactNode; label: string; value: number }[];
}) {
  return (
    <div className="py-0.5 space-y-1.5">
      {segments.map((segment) => (
        <div key={segment.label} className="flex items-center text-xs">
          {segment.icon}
          <span className="mr-8">{segment.label}</span>
          <span className="tabular-nums font-medium ml-auto">{segment.value}</span>
        </div>
      ))}
    </div>
  );
}

export { Tooltip, TooltipProvider, TooltipTrigger, TooltipPopup, TooltipSegments };
